import traverseAndSetObject from "features/shared/functions/traverseAndSetObject";
import { FormEvent } from "react";

import deleteFileWithURL from "services/firebase/deleteFileWithURL";
import uploadFile from "services/firebase/fileUpload";

interface Props<T> {
    fields: T;
    initialFields?: T; // used for form rest
    readOnlyMode?: boolean;
    shouldHandleImages?: boolean;
    handleFormVerification?: (fields: T) => Promise<string>;
    imageUploadOptions?: {
        originalImageUrl?: string;
        fieldsImageUrl?: string;
        imageFile?: File | null;
        submitAccessor: string;
    };
    onSubmitCallback: (fields: T) => void;
}

const useSubmit = <T extends { [key: string]: any }>({
    fields,
    readOnlyMode,
    handleFormVerification,
    imageUploadOptions,
    onSubmitCallback,
}: Props<T>) => {
    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (readOnlyMode) return;

        let uploadedFields = {
            ...fields,
        };

        try {
            if (handleFormVerification)
                await handleFormVerification(uploadedFields);
            if (imageUploadOptions) {
                const uploadedUrl = await getFinalImageUrl();
                traverseAndSetObject(
                    uploadedFields,
                    imageUploadOptions.submitAccessor,
                    uploadedUrl
                );
            }
            onSubmitCallback(uploadedFields);
        } catch (err) {
            alert(err || "Error with uploading");
        }
    };

    const getFinalImageUrl = async () => {
        if (imageUploadOptions) {
            const { fieldsImageUrl, originalImageUrl, imageFile } =
                imageUploadOptions;
            if (imageFile) {
                // if the user has uploaded an image
                const imgUrl = await uploadFile(imageFile);
                if (fieldsImageUrl && imgUrl !== fieldsImageUrl) {
                    // delete old if there exists original image
                    await deleteFileWithURL(fieldsImageUrl);
                }
                return imgUrl;
            }

            // if there is no image file
            if (originalImageUrl) {
                if (fieldsImageUrl !== originalImageUrl) {
                    // if original exists but is deleted by the user
                    await deleteFileWithURL(originalImageUrl);
                    return fieldsImageUrl;
                }
                // if original exists and is left untouched
                return originalImageUrl;
            }

            // if theres nothing at all
            return fieldsImageUrl ?? "";
        }
    };

    return [handleSubmit] as const;
};

export default useSubmit;
